// eslint-disable-next-line sort-imports
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
// eslint-disable-next-line sort-imports
import Collection from "@kaspernj/api-maker/build/collection.js"
// eslint-disable-next-line sort-imports
import useDebouncedCallback from "@restart/hooks/useDebouncedCallback"
// eslint-disable-next-line sort-imports
import {useRef} from "react"
// eslint-disable-next-line sort-imports
import Form from "react-bootstrap/Form"
// eslint-disable-next-line sort-imports
import {useRecoilValue} from "recoil"
// eslint-disable-next-line sort-imports
import memo from "set-state-compare/build/memo.js"
// eslint-disable-next-line sort-imports
import {defaultSelectProps, NemoaSelectV2} from "./nemoa-select-v2"
// eslint-disable-next-line sort-imports
import {tenantAtom} from "shared/atoms/tenant-atom"

export default memo(shapeComponent(class NemoaModelSelectV2 extends ShapeComponent {
  static defaultProps = {
    currentTenantFilter: true
  }

  static propTypes = {
    collection: PropTypes.instanceOf(Collection).isRequired,
    collectionParams: PropTypes.object,
    currentTenantFilter: PropTypes.bool,
    modelToOption: PropTypes.func.isRequired,
    searchParams: PropTypes.string.isRequired
  }

  state = {
    value: undefined,
    options: undefined,
    searchValue: undefined,
    page: 1,
    isOpen: false,
    loading: false
  }

  tenant = this.hookField()
  containerRef = this.hookField()
  debouncedSearch = this.hookField()

  prevCollectionParams = undefined
  prevPage = undefined
  prevSearchValue = undefined
  prevIsOpen = undefined
  prevOptions = undefined
  inFlight = false

  setup() {
    this.tenant = useRecoilValue(tenantAtom)
    this.containerRef = useRef(null)
    this.debouncedSearch = useDebouncedCallback(this.loadOptions, 250)
  }

  selectProps() {
    return defaultSelectProps(this.props)
  }

  loadOptions = () => {
    const {collection, collectionParams, currentTenantFilter, modelToOption, searchParams} = this.selectProps()
    const page = this.s.page
    const searchValue = this.s.searchValue

    if (this.inFlight) {
      return
    }

    this.inFlight = true
    this.s.loading = true

    const query = collection.clone().page(page)

    if (collectionParams) {
      query.ransack(collectionParams)
    }

    if (currentTenantFilter) {
      query.ransack({tenant_id_eq: this.tt.tenant.id()})
    }

    if (searchValue) {
      query.ransack({[searchParams]: searchValue})
    }

    const load = async () => {
      try {
        const result = await query.result()
        const models = result.models()
        const newOptions = models.map(modelToOption)

        if (page > 1) {
          this.setState((state) => ({options: state.options.concat(newOptions)}))
        } else {
          this.setState({options: newOptions})
        }
      } finally {
        this.inFlight = false
        this.s.loading = false
      }
    }

    load()
  }

  onSearch = (value) => {
    this.setState({searchValue: value, page: 1})
  }

  onChange = (option) => {
    const {onChange: onChangeFromProps, isMulti} = this.selectProps()

    if (!isMulti) {
      this.setState({isOpen: false})
    }

    if (onChangeFromProps) {
      onChangeFromProps(option)
    } else {
      this.setState({value: option})
    }
  }

  onMenuOpen = () => {
    this.s.isOpen = true
  }

  onMenuClose = () => {
    this.s.isOpen = false
  }

  // react-select's controlled menuIsOpen does not open the menu on control
  // click in headless Chrome 152, so the menu is opened explicitly when the
  // control area is pressed. The clear indicator, dropdown indicator, and open
  // menu each handle their own mousedown; updating isOpen for them re-renders
  // mid-event and replaces the clear node before its clear handler can fire.
  onControlMouseDown = (event) => {
    const target = event?.target
    const selfHandledSelectors = ".nemoa-select-v2__clear-indicator, .nemoa-select-v2__dropdown-indicator, .nemoa-select-v2__menu"

    if (target && typeof target.closest === "function" && target.closest(selfHandledSelectors)) {
      return
    }

    if (!this.s.isOpen) {
      this.s.isOpen = true
    }
  }

  attachMenuOpenHandler() {
    const el = this.containerRef.current

    if (!el) {
      return
    }

    this.menuOpenHandler = (event) => {
      this.onControlMouseDown(event)
    }
    el.addEventListener("mousedown", this.menuOpenHandler, {capture: true})
  }

  detachMenuOpenHandler() {
    const el = this.containerRef.current

    if (el && this.menuOpenHandler) {
      el.removeEventListener("mousedown", this.menuOpenHandler, {capture: true})
      this.menuOpenHandler = null
    }
  }

  syncValueFromProps() {
    this.setState({value: this.selectProps().value})
  }

  async loadInitialOption() {
    const {collection, defaultValue: defaultValueFromProps, modelToOption} = this.selectProps()

    if (!defaultValueFromProps) {
      return
    }

    const initialModels = await collection.clone().ransack({id_in: defaultValueFromProps}).toArray()

    this.setState({value: initialModels.map((model) => modelToOption(model))[0]})
  }

  componentDidMount() {
    this.syncValueFromProps()
    this.loadInitialOption()
    this.attachMenuOpenHandler()
    this.prevCollectionParams = this.props.collectionParams
    this.prevPage = this.s.page
    this.prevSearchValue = this.s.searchValue
    this.prevIsOpen = this.s.isOpen
    this.prevOptions = this.s.options
  }

  componentWillUnmount() {
    this.detachMenuOpenHandler()
  }

  componentDidUpdate(prevProps) {
    if (defaultSelectProps(prevProps).value !== this.selectProps().value) {
      this.syncValueFromProps()
    }
  }

  refresh() {
    const collectionParams = this.props.collectionParams
    const page = this.s.page
    const searchValue = this.s.searchValue
    const isOpen = this.s.isOpen
    const options = this.s.options

    if (
      collectionParams !== this.prevCollectionParams ||
      page !== this.prevPage ||
      searchValue !== this.prevSearchValue
    ) {
      this.debouncedSearch()
    }

    if (isOpen !== this.prevIsOpen || options !== this.prevOptions) {
      if (!options && isOpen) {
        this.loadOptions()
      }
    }

    this.prevCollectionParams = collectionParams
    this.prevPage = page
    this.prevSearchValue = searchValue
    this.prevIsOpen = isOpen
    this.prevOptions = options
  }

  render() {
    const {id, label, ...restProps} = this.selectProps()
    const {value, options, loading} = this.s

    const select = (
      <div ref={this.containerRef}>
        <NemoaSelectV2
          id={id}
          isLoading={loading}
          onChange={this.onChange}
          onInputChange={this.onSearch}
          onMenuClose={this.onMenuClose}
          onMenuOpen={this.onMenuOpen}
          options={options ?? []}
          value={value}
          {...restProps}
        />
      </div>
    )

    if (label) {
      return (
        <Form.Group>
          <Form.Label htmlFor={id}>
            {label}
          </Form.Label>
          {select}
        </Form.Group>
      )
    }

    return select
  }
}))
